import React, { useCallback, useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, Link } from "react-router-dom";
import { ViewUserAppointment, AssignAgentToAppointment } from "../../Redux/Actions/homeVisitActions";
import { AgentList } from "../../Redux/Actions/agentActions";
import { SendAgentNotification } from "../../Redux/Actions/agentActions";
import { sendNotification } from "../../Redux/Actions/notificationActions";
import { ToFirstCapital } from "../../Utils/helpers";
import { ReactComponent as BackIcon } from "../../Assets/Categories/BackIcon.svg";
import SearchBar from "../Main/searchBar";
import { Skeleton, Modal, Select, message, Tooltip } from "antd";
import "./viewHomeVisit.css";

const { Option } = Select;

/* ── Dummy/default coords to filter out (Google HQ & SF defaults) ── */
const HVV_DUMMY_COORDS = [
  [37.421998333333335, -122.084],
  [37.785834, -122.406417],
];

const isDummyCoord = (lat, lng) =>
  HVV_DUMMY_COORDS.some(
    ([dlat, dlng]) => Math.abs(lat - dlat) < 0.0001 && Math.abs(lng - dlng) < 0.0001
  );

/* ── Leaflet map rendered inside a card (loaded via CDN, no npm dep) ── */
const AgentRouteMap = ({ agentLocations, dropLocation }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
      document.head.appendChild(link);
    }

    const initMap = () => {
      if (mapInstanceRef.current || !mapRef.current) return;
      const L = window.L;

      const validAgentPoints = (agentLocations || [])
        .filter(
          (p) =>
            typeof p?.lat === "number" &&
            typeof p?.lng === "number" &&
            !isDummyCoord(p.lat, p.lng)
        )
        .map((p) => [p.lat, p.lng]);

      const startLatLng = validAgentPoints[0] || null;

      // location is GeoJSON format: coordinates = [lng, lat]
      const dropLatLng =
        dropLocation?.coordinates?.length === 2
          ? [dropLocation.coordinates[1], dropLocation.coordinates[0]]
          : null;

      if (!startLatLng && !dropLatLng) return;

      const allPoints = [
        ...(startLatLng ? [startLatLng] : []),
        ...validAgentPoints,
        ...(dropLatLng ? [dropLatLng] : []),
      ];

      const map = L.map(mapRef.current, { zoomControl: true, scrollWheelZoom: false });
      mapInstanceRef.current = map;

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(map);

      // ── Start marker (green) ──
      if (startLatLng) {
        const startIcon = L.divIcon({
          html: `<div class="hvv-map-marker hvv-map-marker--start">
                   <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                     <circle cx="12" cy="12" r="10"/>
                   </svg>
                 </div>`,
          className: "",
          iconSize: [32, 32],
          iconAnchor: [16, 16],
        });
        L.marker(startLatLng, { icon: startIcon })
          .addTo(map)
          .bindPopup("<b>Start Location</b>");
      }

      // ── Agent trail polyline ──
      if (validAgentPoints.length > 1) {
        L.polyline(validAgentPoints, {
          color: "#0D152B",
          weight: 3,
          opacity: 0.75,
          dashArray: "6 4",
        }).addTo(map);
      }

      // ── Agent location dots (numbered) ──
      validAgentPoints.forEach((point, idx) => {
        const isLast = idx === validAgentPoints.length - 1;
        const dotIcon = L.divIcon({
          html: `<div class="hvv-map-dot-numbered ${isLast ? "hvv-map-dot-numbered--last" : ""}">
                   <span class="hvv-map-dot-number">${idx + 1}</span>
                 </div>`,
          className: "",
          iconSize: [22, 22],
          iconAnchor: [11, 11],
        });
        L.marker(point, { icon: dotIcon })
          .addTo(map)
          .bindPopup(isLast ? `<b>Last Known Position</b><br/>Point ${idx + 1}` : `Point ${idx + 1}`);
      });

      // ── Drop location marker (orange pin) ──
      if (dropLatLng) {
        const dropIcon = L.divIcon({
          html: `<div class="hvv-map-marker hvv-map-marker--drop">
                   <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                     <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/>
                     <circle cx="12" cy="10" r="3" fill="white"/>
                   </svg>
                 </div>`,
          className: "",
          iconSize: [32, 32],
          iconAnchor: [16, 16],
        });
        L.marker(dropLatLng, { icon: dropIcon })
          .addTo(map)
          .bindPopup(
            `<b>Drop Location</b><br/><span style="font-size:11px;color:#666">${dropLatLng[0].toFixed(6)}, ${dropLatLng[1].toFixed(6)}</span>`
          );
      }

      // ── Fit bounds ──
      if (allPoints.length > 1) {
        map.fitBounds(L.latLngBounds(allPoints), { padding: [40, 40] });
      } else {
        map.setView(allPoints[0], 16);
      }
    };

    if (window.L) {
      initMap();
    } else {
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
      script.onload = initMap;
      document.head.appendChild(script);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [agentLocations, dropLocation]);

  return <div ref={mapRef} className="hvv-map-container" />;
};

/* ═══════════════════════════════════════════════════════════ */

const ViewHomeVisit = () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [agentPagination, setAgentPagination] = useState({
    current: 1,
    pageSize: 500,
  });
  const [refreshingLocation, setRefreshingLocation] = useState(false);

  const appointment = useSelector((state) => state.homeVisit || {});
  const agentState = useSelector((state) => state.agents || {});

  const loading = appointment?.loading || appointment?.assigningAgent;
  const data = appointment?.appointment || {};
  const agentsData = agentState?.agents || {};
  const assigningAgent = appointment?.assigningAgent || false;
  // const assignmentError = appointment?.assignmentError;

  const { admin } = useSelector((state) => state.admin || {});
  const accessCode = admin?.data?.access_code || [];
  const canAdd = Array.isArray(accessCode) && accessCode.includes('add');
  const canEdit = Array.isArray(accessCode) && accessCode.includes('edit');


  const latitude = data?.location?.coordinates?.[1];
  const longitude = data?.location?.coordinates?.[0];

  const hasValidCoords = (loc) =>
    loc && typeof loc.lat === "number" && typeof loc.lng === "number";

  const latestAgentLocation = (() => {
    const history = Array.isArray(data?.agent_location) ? data.agent_location : [];
    if (history.length) {
      return history.reduce((latest, curr) => {
        if (!latest?.updated_at) return curr;
        if (!curr?.updated_at) return latest;
        return new Date(curr.updated_at) > new Date(latest.updated_at) ? curr : latest;
      }, null);
    }
    return data?.agent_current_location || null;
  })();

  // Trail points used for the Route Map coords summary (dummy coords filtered out)
  const routeTrail = (Array.isArray(data?.agent_location) ? data.agent_location : [])
    .filter((p) => hasValidCoords(p) && !isDummyCoord(p.lat, p.lng))
    .slice()
    .sort((a, b) => new Date(a.updated_at) - new Date(b.updated_at));

  const routeStart = routeTrail[0] || null;
  const routeLast = routeTrail[routeTrail.length - 1] || null;

  const dropLocation =
    Array.isArray(data?.location?.coordinates) && data.location.coordinates.length === 2
      ? { lat: data.location.coordinates[1], lng: data.location.coordinates[0] }
      : null;

  const franchiseTeam = [
    { label: "Franchise Manager", person: data?.franchisy_id },
    { label: "City Head", person: data?.city_head },
    { label: "Locality Head", person: data?.locality_head },
  ].filter(({ person }) => person?.name);

  const fetchAgentsList = useCallback(async () => {
    const agentParams = {
      page: agentPagination.current,
      limit: agentPagination.pageSize,
      is_deleted: false,
      role: ["exclusive partner"],
    };
    dispatch(AgentList(agentParams, false));
  }, [dispatch, agentPagination]);

  useEffect(() => {
    if (id) {
      dispatch(ViewUserAppointment(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    fetchAgentsList();
  }, [fetchAgentsList]);


  const handleAgentSelect = async (agentId) => {
    if (!agentId) {
      message.error("Please select an agent");
      return;
    }

    const selectedAgent = agentsData?.data?.find((agent) => agent._id === agentId);
    if (!selectedAgent) {
      message.error("Agent not found");
      return;
    }

    Modal.confirm({
      title: "Confirm Agent Assignment",
      icon: null,
      wrapClassName: "hvv-confirm-modal",
      content: (
        <div className="hvv-confirm-content">
          <p className="hvv-confirm-text">
            Are you sure you want to assign this appointment to:
          </p>
          <div className="hvv-confirm-agent-box">
            <span className="hvv-confirm-agent-avatar">
              {selectedAgent.name?.charAt(0)?.toUpperCase() || "A"}
            </span>
            <div className="hvv-confirm-agent-info">
              <p className="hvv-confirm-agent-name">{selectedAgent.name}</p>
              <p className="hvv-confirm-agent-id">{selectedAgent.staff_id}</p>
            </div>
          </div>
          <p className="hvv-confirm-note">
            This action cannot be undone. The agent will be **notified via push notification**.
          </p>
        </div>
      ),
      okText: "Assign",
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        try {
          const notificationPayload = {
            agent_id: agentId,
            title: "New Home Visit Assigned",
            message: `New home visit appointment assigned to you. User: ${data?.user?.name || data?.user?.user_id || 'N/A'}. Check details now!`,
            type_name: "HOME_VISIT_ASSIGNMENT",
            type_id: id,
            type_data: {
              appointment_id: id,
              user_id: data?.user?._id,
              user_name: data?.user?.name,
              appointment_date: data?.appointment_date
            }
          };

          const userNotificationPayload = {
            title: "Agent Assigned",
            message: `Your home visit has been scheduled with ${selectedAgent.name} on ${new Date(data?.appointment_date).toLocaleDateString("en-US", { dateStyle: "medium" })}. The agent will assist you accordingly.`,
            type_name: "home_visit_agent_assigned",
            type_id: id,

            type_data: {
              appointment_id: id,
              agent_id: agentId,
              agent_name: selectedAgent.name,
              appointment_date: data?.appointment_date,
            },

            notification_for: "user",
            type: "selected",
            list: [data?.user?._id],
          };


          await dispatch(
            AssignAgentToAppointment({
              user_appointment_id: id,
              user: data?.user?._id,
              agent: agentId,
            })
          );

          dispatch(SendAgentNotification(notificationPayload));

          dispatch(sendNotification(userNotificationPayload));

          message.success("Agent assigned successfully!");

          setTimeout(() => {
            dispatch(ViewUserAppointment(id));
          }, 500);
        } catch (error) {
          console.error("Assign agent error:", error);
          message.error("Failed to assign agent. Please try again.");
          throw error;
        }
      },
      onCancel() { },
    });
  };

  const handleRefreshLocation = async () => {
    setRefreshingLocation(true);
    try {
      await dispatch(ViewUserAppointment(id));
    } finally {
      setRefreshingLocation(false);
    }
  };


  // const handleAgentPaginationChange = (pagination) => {
  //   setAgentPagination(pagination);
  // };

  return (
    <div className="hvv-container">
      {/* Header */}
      <div className="hvv-searchbar-wrapper">
        <div className="hvv-searchbar-container">
          <SearchBar />
        </div>
      </div>

      <div className="hvv-header back-icon margin-0 gap-10 align-center flex-container">
        <Link to="/home_visit">
          <BackIcon style={{ cursor: "pointer" }} />
        </Link>
        <div className="hvv-title-section referral-view">
          <h3 className="hvv-main-title">User Home Visit Details</h3>
          <h4 className="hvv-breadcrumb">Home Visit / List / View</h4>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="hvv-content">
          <Skeleton active paragraph={{ rows: 10 }} />
        </div>
      ) : (
        <div className="hvv-content">
          {/* Profile summary card */}
          <div className="hvv-profile-card">
            <div className="hvv-profile-main">
              <div className="hvv-avatar-placeholder">
                {data?.user?.user_id?.charAt(0) || "U"}
              </div>

              <div className="hvv-profile-info">
                <h1 className="hvv-user-name">
                  {data?.user?.user_id || "N/A"}
                </h1>
                <div className="hvv-otp-badge">
                  OTP: {data?.otp || "—"}
                </div>
              </div>
            </div>

            <div className="hvv-status-control">
              <div className="hvv-status-group">
                <label className="hvv-status-label">Payment Status</label>
                <div className={`hvv-status-badge hvv-status-${data?.payment_status?.toLowerCase() || "pending"}`}>
                  {ToFirstCapital(data?.payment_status) || "Pending"}
                </div>
              </div>
              <div className="hvv-status-group">
                <label className="hvv-status-label">Status</label>
                <div className={`hvv-status-badge hvv-status-${data?.status?.toLowerCase() || "pending"}`}>
                  {ToFirstCapital(data?.status) || "Pending"}
                </div>
              </div>
            </div>
          </div>

          {/* Details Grid */}
          <div className="hvv-details-grid">
            {/* User Details Card */}
            <div className="hvv-card">
              <h3 className="hvv-card-title"><span className="hvv-card-title-badge">01</span>User Details</h3>

              <div className="hvv-card-content">
                <div className="hvv-info-row">
                  <span className="hvv-info-label">Name</span>
                  <span className="hvv-info-value">{data?.user?.name || "—"}</span>
                </div>

                <div className="hvv-info-row">
                  <span className="hvv-info-label">Email</span>
                  <span className="hvv-info-value">{data?.user?.email || "—"}</span>
                </div>

                <div className="hvv-info-row">
                  <span className="hvv-info-label">User ID</span>
                  <span className="hvv-info-value">{data?.user?.user_id || "—"}</span>
                </div>

                <div className="hvv-info-row">
                  <span className="hvv-info-label">Phone</span>
                  <span className="hvv-info-value">
                    {data?.user?.country_code && data?.user?.contact_number
                      ? `${data.user.country_code} ${data.user.contact_number}`
                      : "—"}
                  </span>
                </div>

                {data?.user?.profile_image && (
                  <div className="hvv-info-row">
                    <span className="hvv-info-label">Profile Image</span>
                    <img
                      src={data.user.profile_image}
                      alt={data.user.name}
                      className="hvv-user-image"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Assigned Agent */}
            <div className="hvv-card">
              <h3 className="hvv-card-title"><span className="hvv-card-title-badge">02</span>Assigned Agent</h3>

              <div className="hvv-card-content">
                {data?.agent ? (
                  <>
                    <div className="hvv-info-row">
                      <span className="hvv-info-label">Name</span>
                      <span className="hvv-info-value">{data.agent.name}</span>
                    </div>

                    <div className="hvv-info-row">
                      <span className="hvv-info-label">Agent ID</span>
                      <span className="hvv-info-value">{data.agent.staff_id}</span>
                    </div>

                    <div className="hvv-info-row">
                      <span className="hvv-info-label">Email</span>
                      <span className="hvv-info-value">{data.agent.email}</span>
                    </div>

                    <div className="hvv-info-row">
                      <span className="hvv-info-label">Phone</span>
                      <span className="hvv-info-value">
                        {data.agent.country_code} {data.agent.contact_number}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="hvv-info-row">
                    <span className="hvv-info-value">No agent assigned</span>
                  </div>
                )}

                {franchiseTeam.length > 0 && (
                  <div className="hvv-franchise-list">
                    <span className="hvv-info-label">Franchise Hierarchy</span>
                    {franchiseTeam.map(({ label, person }) => (
                      <div key={person._id} className="hvv-franchise-item">
                        <div className="hvv-franchise-avatar">{person.name.charAt(0).toUpperCase()}</div>
                        <div className="hvv-franchise-info">
                          <div className="hvv-franchise-role">{label}</div>
                          <div className="hvv-franchise-name">{person.name}</div>
                          <Tooltip title={person.email}>
                            <div className="hvv-franchise-email">{person.email || "—"}</div>
                          </Tooltip>
                        </div>
                        {person.contact_number && (
                          <span className="hvv-franchise-phone">
                            {person.country_code || ""} {person.contact_number}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Appointment Info Card */}
            <div className="hvv-card">
              <h3 className="hvv-card-title"><span className="hvv-card-title-badge">03</span>Appointment Details</h3>

              <div className="hvv-card-content">
                <div className="hvv-info-row">
                  <span className="hvv-info-label">Date</span>
                  <span className="hvv-info-value">
                    {data?.appointment_date
                      ? new Date(data.appointment_date).toLocaleDateString("en-US", {
                        dateStyle: "medium",
                      })
                      : "—"}
                  </span>
                </div>

                <div className="hvv-info-row">
                  <span className="hvv-info-label">Time Slot</span>
                  <span className="hvv-info-value">
                    {data?.time_slot ? data?.time_slot : "—"}
                  </span>
                </div>

                <div className="hvv-info-row">
                  <span className="hvv-info-label">Rating</span>
                  <span className="hvv-info-value">
                    {data?.rating > 0 ? (
                      <span className="hvv-star-rating">
                        {Array.from(
                          { length: data.rating_by_user_to_agent },
                          (_, i) => (
                            <span key={i}>⭐</span>
                          )
                        )}
                      </span>
                    ) : (
                      "—"
                    )}
                  </span>
                </div>

                <div className="hvv-info-row">
                  <span className="hvv-info-label">Created At</span>
                  <span className="hvv-info-value">
                    {data?.created_at
                      ? new Date(data.created_at).toLocaleString("en-US", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })
                      : "—"}
                  </span>
                </div>

                <div className="hvv-review-wrapper">
                  <div className="hvv-review-label-row">
                    <span className="hvv-info-label">Review of user</span>
                    {data?.review_by_user_to_agent && data.review_by_user_to_agent !== "N/A" && (
                      <button
                        type="button"
                        className="hvv-review-expand-btn"
                        onClick={() => setIsReviewModalOpen(true)}
                        title="Expand review"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <div className="hvv-review-box">
                    {data?.review_by_user_to_agent && data.review_by_user_to_agent !== "N/A" ? (
                      <p className="hvv-review-text">
                        {data.review_by_user_to_agent}
                      </p>
                    ) : (
                      <p className="hvv-review-text hvv-review-text--empty">—</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Address Card */}
            <div className="hvv-card">
              <h3 className="hvv-card-title"><span className="hvv-card-title-badge">04</span>Location Details</h3>
              <div className="hvv-card-content">
                <div className="hvv-info-row">
                  <span className="hvv-info-label">Saved As</span>
                  <span className="hvv-info-value">
                    {ToFirstCapital(data?.address?.save_as) || "—"}
                  </span>
                </div>

                <div className="hvv-info-row">
                  <span className="hvv-info-label">Address</span>
                  <span className="hvv-info-value">
                    {data?.address?.address || "—"}
                  </span>
                </div>

                <div className="hvv-info-row">
                  <span className="hvv-info-label">House/Flat</span>
                  <span className="hvv-info-value">
                    {data?.address?.house_flat_floorno || "—"}
                  </span>
                </div>

                <div className="hvv-info-row">
                  <span className="hvv-info-label">Area</span>
                  <span className="hvv-info-value">
                    {data?.address?.appartment_road_area || "—"}
                  </span>
                </div>

                <div className="hvv-info-row">
                  <span className="hvv-info-label">Directions</span>
                  <span className="hvv-info-value">
                    {data?.address?.direction_to_reach || "—"}
                  </span>
                </div>

                {latitude && longitude && (
                  <div className="hvv-map">
                    <iframe
                      title="Location Map"
                      src={`https://www.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`}
                      width="100%"
                      height="280"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                )}

                {data?.address?.set_as_default && (
                  <div className="hvv-info-row">
                    <span className="hvv-info-label">Default Address</span>
                    <span className="hvv-info-value">✓ Yes</span>
                  </div>
                )}

                <div className="hvv-partner-location">
                  <div className="hvv-partner-location-head">
                    <span className="hvv-info-label">Partner Location</span>
                    <button
                      type="button"
                      className="hvv-refresh-btn"
                      onClick={handleRefreshLocation}
                      disabled={refreshingLocation}
                      title="Refresh partner location"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                        className={refreshingLocation ? "hvv-refresh-spin" : ""}>
                        <path d="M23 4v6h-6M1 20v-6h6" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>

                  <div className="hvv-info-row">
                    <span className="hvv-info-label">Last Updated</span>
                    <span className="hvv-info-value">
                      {latestAgentLocation?.updated_at
                        ? new Date(latestAgentLocation.updated_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })
                        : "N/A"}
                    </span>
                  </div>

                  {hasValidCoords(latestAgentLocation) ? (
                    <div className="hvv-map">
                      <iframe
                        title="Partner Location Map"
                        src={`https://www.google.com/maps?q=${latestAgentLocation.lat},${latestAgentLocation.lng}&z=15&output=embed`}
                        width="100%"
                        height="280"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </div>
                  ) : (
                    <div className="hvv-info-row">
                      <span className="hvv-info-value">N/A</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Projects + Agent assignment */}
            <div className="hvv-card hvv-card--full">
              <h3 className="hvv-card-title"><span className="hvv-card-title-badge">05</span>Projects</h3>

              <div className="hvv-workspace">
                <div className="hvv-project-list">
                  {data?.projects?.length > 0 ? (
                    data.projects.map((proj) => (
                      <div key={proj._id} className="hvv-project-item">
                        <div className="hvv-project-row">
                          <label className="hvv-project-label">Project Name</label>
                          <span className="hvv-project-value">{proj.project_name}</span>
                        </div>
                        <div className="hvv-project-row">
                          <label className="hvv-project-label">Brand</label>
                          <span className="hvv-project-value">{proj.brand_name}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="hvv-empty-state">
                      <p className="hvv-empty-text">No projects assigned</p>
                    </div>
                  )}
                </div>

                {canAdd && canEdit &&
                  <div className="hvv-assign-box">
                    <div className="hvv-assign-head">
                      <h4 className="hvv-assign-title">Assign Agent</h4>
                      {data?.status === "requested" && !data?.agent ? (
                        <span className="hvv-assign-badge hvv-assign-badge--available">Available</span>
                      ) : data?.agent ? (
                        <span className="hvv-assign-badge hvv-assign-badge--assigned">Assigned</span>
                      ) : (
                        <span className="hvv-assign-badge hvv-assign-badge--completed">Completed</span>
                      )}
                    </div>

                    <div className="hvv-assign-body">
                      <Select
                        className="hvv-staff-select"
                        value={data?.agent?._id || data?.agent || undefined}
                        placeholder={
                          data?.agent
                            ? (() => {
                              const assignedAgent = agentsData?.data?.find(
                                (agent) => agent._id === (data.agent?._id || data.agent)
                              );
                              return assignedAgent
                                ? <span>Assigned: {assignedAgent.name}</span>
                                : "Select agent to assign";
                            })()
                            : "Select agent to assign"
                        }
                        loading={agentState.loading || assigningAgent}
                        onChange={handleAgentSelect}
                        allowClear={!data?.agent}
                        optionLabelProp="label"
                        showSearch
                        filterOption={(input, option) =>
                          (option?.children?.props?.children[0]?.props?.children[0] || '')
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        {Array.isArray(agentsData?.data) &&
                          agentsData.data.map((agent) => (
                            <Option
                              key={agent._id}
                              value={agent._id}
                              label={`${agent.name} (${agent.staff_id})`}
                            >
                              <div className="hvv-option-row">
                                <div className="hvv-option-left">
                                  <span className="hvv-option-name">{agent.name}</span>
                                  <span className="hvv-option-id">{agent.staff_id}</span>
                                </div>
                                <span className="hvv-option-category">{agent.category}</span>
                              </div>
                            </Option>
                          ))}
                      </Select>

                      {data?.agent && (
                        <div className="hvv-assigned-note">
                          ✅ Assigned to: {data.agent.name} ({data.agent.staff_id})
                        </div>
                      )}
                    </div>
                  </div>
                }
              </div>
            </div>

            {/* Agent Route Map */}
            {(routeTrail.length > 0 || hasValidCoords(dropLocation)) && (
              <div className="hvv-card hvv-card--full">
                <h3 className="hvv-card-title"><span className="hvv-card-title-badge">06</span>Agent Route Map</h3>

                <div className="hvv-card-content">
                  {/* Legend */}
                  <div className="hvv-map-legend">
                    <div className="hvv-map-legend-item">
                      <span className="hvv-map-legend-dot hvv-map-legend-dot--start" />
                      Start Location
                    </div>
                    <div className="hvv-map-legend-item">
                      <span className="hvv-map-legend-dot hvv-map-legend-dot--trail" />
                      Agent Trail
                    </div>
                    <div className="hvv-map-legend-item">
                      <span className="hvv-map-legend-dot hvv-map-legend-dot--last" />
                      Last Position
                    </div>
                    {hasValidCoords(dropLocation) && (
                      <div className="hvv-map-legend-item">
                        <span className="hvv-map-legend-dot hvv-map-legend-dot--drop" />
                        Drop Location
                      </div>
                    )}
                  </div>

                  <AgentRouteMap
                    agentLocations={data?.agent_location}
                    dropLocation={data?.location}
                  />

                  {/* Coords summary */}
                  <div className="hvv-map-coords">
                    {routeStart && (
                      <div className="hvv-map-coord-item">
                        <span className="hvv-map-coord-label">Start</span>
                        <span className="hvv-map-coord-val">
                          {routeStart.lat.toFixed(6)}, {routeStart.lng.toFixed(6)}
                        </span>
                      </div>
                    )}
                    {hasValidCoords(dropLocation) && (
                      <div className="hvv-map-coord-item">
                        <span className="hvv-map-coord-label">Drop</span>
                        <span className="hvv-map-coord-val">
                          {dropLocation.lat.toFixed(6)}, {dropLocation.lng.toFixed(6)}
                        </span>
                      </div>
                    )}
                    {routeLast && (
                      <div className="hvv-map-coord-item">
                        <span className="hvv-map-coord-label">Last</span>
                        <span className="hvv-map-coord-val">
                          {routeLast.lat.toFixed(6)}, {routeLast.lng.toFixed(6)}
                        </span>
                      </div>
                    )}
                    <div className="hvv-map-coord-item">
                      <span className="hvv-map-coord-label">Points</span>
                      <span className="hvv-map-coord-val">{routeTrail.length} tracked</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Nearby Agents List */}
            {data?.agent_list?.length > 0 && (
              <div className="hvv-card hvv-card--full">
                <div className="hvv-card-title hvv-card-title--split">
                  <span className="hvv-card-title-left">
                    <span className="hvv-card-title-badge">07</span>Nearby Agents List
                  </span>
                  <Link to="/agent">
                    <button type="button" className="hvv-btn-ghost">View All</button>
                  </Link>
                </div>
                <div className="hvv-card-content">
                  <div className="hvv-agents-grid">
                    {data.agent_list.map((agent) => (
                      <div key={agent._id} className="hvv-agent-tile">
                        <div className="hvv-agent-tile-header">
                          {agent.profile_image ? (
                            <img
                              src={agent.profile_image}
                              alt={agent.name}
                              className="hvv-agent-tile-avatar"
                            />
                          ) : (
                            <div className="hvv-agent-tile-avatar hvv-agent-tile-avatar--placeholder">
                              {agent.name?.charAt(0) || "A"}
                            </div>
                          )}
                          <div className="hvv-agent-tile-info">
                            <h4 className="hvv-agent-tile-name">{agent.name}</h4>
                            <span className="hvv-agent-tile-id">
                              Agent Id: {agent.staff_id}
                            </span>
                          </div>
                        </div>
                        <div className="hvv-agent-tile-contact">
                          <div className="hvv-contact-row">
                            <span className="hvv-contact-label">Email</span>
                            <span className="hvv-contact-value">{agent.email}</span>
                          </div>
                          <div className="hvv-contact-row">
                            <span className="hvv-contact-label">Phone</span>
                            <span className="hvv-contact-value">
                              {agent.country_code} {agent.contact_number}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )
      }

      <Modal
        title="User Review"
        open={isReviewModalOpen}
        footer={null}
        centered
        onCancel={() => setIsReviewModalOpen(false)}
      >
        <p style={{ whiteSpace: "pre-wrap", lineHeight: "1.6" }}>
          {data?.review_by_user_to_agent || "No review available"}
        </p>
      </Modal>
    </div >
  );
};

export default ViewHomeVisit;